import { User, UserRole } from '../types/user';
import { authService } from '../services/auth';

// Role hierarchy (higher roles have access to lower role features)
const ROLE_HIERARCHY: Record<UserRole, number> = {
  'guest': 1,
  'operator': 2,
  'admin': 3,
};

// Permission definitions
export const PERMISSIONS = {
  // User permissions
  VIEW_PROFILE: ['guest', 'operator', 'admin'] as UserRole[],
  UPDATE_PROFILE: ['guest', 'operator', 'admin'] as UserRole[],
  VIEW_BOOKINGS: ['guest', 'operator', 'admin'] as UserRole[],
  CREATE_BOOKINGS: ['guest', 'operator', 'admin'] as UserRole[],
  
  // Operator permissions
  MANAGE_LOCATIONS: ['operator', 'admin'] as UserRole[],
  MANAGE_SERVICES: ['operator', 'admin'] as UserRole[],
  VIEW_OPERATOR_ANALYTICS: ['operator', 'admin'] as UserRole[],
  UPDATE_OPERATOR_PROFILE: ['operator', 'admin'] as UserRole[],
  
  // Admin permissions
  MANAGE_USERS: ['admin'] as UserRole[],
  MANAGE_OPERATORS: ['admin'] as UserRole[],
  MANAGE_ALL_LOCATIONS: ['admin'] as UserRole[],
  MANAGE_ALL_SERVICES: ['admin'] as UserRole[],
  MANAGE_ALL_BOOKINGS: ['admin'] as UserRole[],
  VIEW_ADMIN_DASHBOARD: ['admin'] as UserRole[],
  MANAGE_BLOG: ['admin'] as UserRole[],
  MANAGE_SUPPORT: ['admin'] as UserRole[],
} as const;

// Dashboard routes for each role
export const DASHBOARD_ROUTES = {
  guest: '/guest-dashboard',
  operator: '/operator-admin-simple',
  admin: '/admin',
} as const;

// API endpoint prefixes for each role (now without /api/ prefix)
export const API_PREFIXES = {
  guest: 'user',
  operator: 'operator',
  admin: 'admin',
} as const;

/**
 * Check if user has a specific permission
 */
export function hasPermission(user: User | null, permission: keyof typeof PERMISSIONS): boolean {
  if (!user) return false;
  
  const userRoles = authService.getUserRoles(user);
  const requiredRoles = PERMISSIONS[permission];
  
  return userRoles.some(role => requiredRoles.includes(role));
}

/**
 * Check if user has any of the specified permissions
 */
export function hasAnyPermission(user: User | null, permissions: (keyof typeof PERMISSIONS)[]): boolean {
  return permissions.some(permission => hasPermission(user, permission));
}

/**
 * Check if user has all of the specified permissions
 */
export function hasAllPermissions(user: User | null, permissions: (keyof typeof PERMISSIONS)[]): boolean {
  return permissions.every(permission => hasPermission(user, permission));
}

/**
 * Get user's primary role
 */
export function getPrimaryRole(user: User | null): UserRole {
  if (!user) return 'guest';
  return authService.getPrimaryRole(user);
}

/**
 * Check if user has a specific role
 */
export function hasRole(user: User | null, role: UserRole): boolean {
  if (!user) return false;
  return authService.hasRole(user, role);
}

/**
 * Check if user has any of the specified roles
 */
export function hasAnyRole(user: User | null, roles: UserRole[]): boolean {
  if (!user) return false;
  return authService.hasAnyRole(user, roles);
}

/**
 * Check if user has a role with higher or equal hierarchy
 */
export function hasRoleOrHigher(user: User | null, requiredRole: UserRole): boolean {
  if (!user) return false;
  
  const userRoles = authService.getUserRoles(user);
  const requiredLevel = ROLE_HIERARCHY[requiredRole];
  
  return userRoles.some(role => ROLE_HIERARCHY[role] >= requiredLevel);
}

/**
 * Get the appropriate dashboard route for a user
 */
export function getDashboardRoute(user: User | null): string {
  if (!user) return '/login';
  
  const primaryRole = getPrimaryRole(user);
  return DASHBOARD_ROUTES[primaryRole] || '/dashboard';
}

/**
 * Get the appropriate API prefix for a user
 */
export function getApiPrefix(user: User | null): string {
  if (!user) return 'user';
  
  const primaryRole = getPrimaryRole(user);
  return API_PREFIXES[primaryRole] || 'user';
}

/**
 * Check if user can access a specific route
 */
export function canAccessRoute(user: User | null, route: string): boolean {
  if (!user) return false;
  
  // Public routes that don't require authentication
  const publicRoutes = ['/', '/login', '/register', '/verify-email', '/blog', '/about'];
  if (publicRoutes.includes(route)) return true;
  
  const primaryRole = getPrimaryRole(user);
  
  // Admin routes
  if (route.startsWith('/admin')) {
    return hasRole(user, 'admin');
  }
  
  // Operator routes
  if (route.startsWith('/operator') || route.includes('operator-admin')) {
    return hasAnyRole(user, ['operator', 'admin']);
  }
  
  // User routes
  if (route.startsWith('/dashboard') || route.startsWith('/profile')) {
    return hasAnyRole(user, ['guest', 'operator', 'admin']);
  }
  
  // Default to allowing access for authenticated users
  return true;
}

/**
 * Get all permissions for a user
 */
export function getUserPermissions(user: User | null): (keyof typeof PERMISSIONS)[] {
  if (!user) return [];
  
  const userRoles = authService.getUserRoles(user);
  const permissions: (keyof typeof PERMISSIONS)[] = [];
  
  Object.entries(PERMISSIONS).forEach(([permission, requiredRoles]) => {
    if (userRoles.some(role => requiredRoles.includes(role))) {
      permissions.push(permission as keyof typeof PERMISSIONS);
    }
  });
  
  return permissions;
}

/**
 * Get role-based navigation items for a user
 */
export function getNavigationItems(user: User | null) {
  if (!user) return [];
  
  const primaryRole = getPrimaryRole(user);
  
  const navigationItems = {
    guest: [
      { name: 'Dashboard', href: '/dashboard', icon: 'Home' },
  
      { name: 'My Bookings', href: '/bookings', icon: 'Calendar' },
      { name: 'Profile', href: '/profile', icon: 'User' },
    ],
    operator: [
      { name: 'Dashboard', href: '/operator-admin-simple', icon: 'BarChart' },
      { name: 'Locations', href: '/operator/locations', icon: 'MapPin' },
      { name: 'Services', href: '/operator/services', icon: 'Settings' },
      { name: 'Bookings', href: '/operator/bookings', icon: 'Calendar' },
      { name: 'Analytics', href: '/operator/analytics', icon: 'TrendingUp' },
      { name: 'Profile', href: '/operator/profile', icon: 'User' },
    ],
    admin: [
      { name: 'Dashboard', href: '/admin', icon: 'BarChart3' },
      { name: 'Users', href: '/admin/users', icon: 'Users' },
      { name: 'Operators', href: '/admin/operators', icon: 'Building2' },
      { name: 'Locations', href: '/admin/locations', icon: 'MapPin' },
      { name: 'Services', href: '/admin/services', icon: 'Settings' },
      { name: 'Bookings', href: '/admin/bookings', icon: 'Calendar' },
      { name: 'Blog', href: '/admin/blogs', icon: 'FileText' },
      { name: 'Support', href: '/admin/support', icon: 'MessageSquare' },
    ],
  };
  
  return navigationItems[primaryRole] || navigationItems.guest;
}

/**
 * Redirect user to appropriate dashboard based on role
 */
export function redirectToDashboard(user: User | null): string {
  return getDashboardRoute(user);
}

/**
 * Check if user should be redirected after login
 */
export function shouldRedirectAfterLogin(user: User | null, currentPath: string): boolean {
  if (!user) return false;
  
  const dashboardRoute = getDashboardRoute(user);
  
  // Don't redirect if already on appropriate dashboard
  if (currentPath === dashboardRoute) return false;
  
  // Don't redirect if on public pages
  const publicPaths = ['/', '/login', '/register', '/verify-email'];
  if (publicPaths.includes(currentPath)) return true;
  
  // Redirect if on wrong dashboard
  const primaryRole = getPrimaryRole(user);
  if (primaryRole === 'admin' && !currentPath.startsWith('/admin')) return true;
  if (primaryRole === 'operator' && !currentPath.includes('operator')) return true;
  if (primaryRole === 'guest' && currentPath.startsWith('/admin')) return true;
  
  return false;
} 